<template>
  <div class="message-content-item">
    <el-button v-if="is_loading" type="text" :loading="is_loading"></el-button>
    <div class="message-content-item-wrap" :class="{'is-merchant-item': props.type === 'merchant'}" v-else>
      <collection-images v-if="content_type === 20 && !has_deleted" :image="cover_image_url" :images="first_4_product_thumbnails" />
      <el-image v-else-if="!has_deleted" :src="cover_image_url" class="post-image" fill="cover" />
      <div class="message-content-item-details" v-if="!has_deleted">
        <h3>{{ name }}</h3>
        <time v-if="date">{{ moment(date).format('MMM DD, YYYY') }}</time>
        <router-link v-if="!is_hide_link && props.type !== 'merchant'" :to="`/manage/${route.params.id}/${content_type === 20 ? 'collections' : 'posts'}?${is_view ? 'view' : 'edit'}-${content_type === 20 ? 'collection' : 'post'}=${content_id}`" class="ui-link">{{ is_view ? 'View' : 'Edit' }} {{ content_type === 20 ? 'collection' : 'post' }}</router-link>
        <router-link v-else-if="props.type === 'merchant'" :to="`/creator/list?id=${content_data.anchor_creator_id}&type=${content_type === 20 ? 'collection' : 'post'}&content_id=${content_id}`" class="ui-link">View</router-link>
      </div>
      <div v-if="has_deleted" class="deleted-message-content-item-details">
        <p>This content has been deleted.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted } from 'vue';
  import { ElImage, ElButton } from 'element-plus';
  import CollectionImages from '@/components/ui/CollectionImages.vue';
  import moment from 'moment';
  import { useRoute } from 'vue-router';

  const route = useRoute();

  const props = defineProps({
    is_view: {
      type: Boolean,
      default: false
    },
    is_hide_link: {
      type: Boolean,
      default: false
    },
    content_type: {
      type: Number,
      required: true
    },
    content_id: {
      type: Number,
      required: true
    },
    merchant_id: {
      type: Number,
      default: 0
    },
    ajax: {
      type: Function,
      required: true
    },
    type: {
      type: String,
      default: ''
    }
  });

  const is_loading = ref(true);
  const cover_image_url = ref('');
  const first_4_product_thumbnails = ref([]);
  const content_data = ref({});
  const name = ref('');
  const date = ref('');
  const has_deleted = ref(false);

  // Generate cache key
  const getCacheKey = () => {
    return `content_${props.content_type === 20 ? 'collection' : 'post'}_${props.content_id}_${props.merchant_id}`;
  };

  // Load data from cache
  const loadFromCache = () => {
    try {
      const cacheKey = getCacheKey();
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const data = JSON.parse(cachedData);
        // Check if cache is expired (24 hours)
        const cacheTime = new Date(data.timestamp);
        const now = new Date();
        const hoursDiff = (now - cacheTime) / (1000 * 60 * 60);

        // Check if cache is still valid
        if (hoursDiff < 24) {
          // Check if content is marked as deleted
          if (data.isDeleted) {
            has_deleted.value = true;
            name.value = 'Content has been deleted';
            is_loading.value = false;
            return true;
          } else {
            handleContent(data.content);
            is_loading.value = false;
            return true;
          }
        } else {
          // Cache expired, remove it
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.warn('Failed to load from cache:', error);
    }
    return false;
  };

  // Save data to cache
  const saveToCache = (data) => {
    try {
      const cacheKey = getCacheKey();
      const cacheData = {
        content: data,
        timestamp: new Date().toISOString(),
        isDeleted: false
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save to cache:', error);
    }
  };

  // Mark content as deleted in cache
  const markAsDeleted = () => {
    try {
      const cacheKey = getCacheKey();
      const cacheData = {
        content: null,
        timestamp: new Date().toISOString(),
        isDeleted: true
      };
      has_deleted.value = true;
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to mark as deleted in cache:', error);
    }
  };

  onMounted(() => {
    // Try to load from cache first
    if (!loadFromCache()) {
      handleLoadContent();
    }
  });

  const handleContent = (responseData) => {

    // console.log('responseData', responseData)
    content_data.value = responseData;

    if (props.content_type === 20) {
      cover_image_url.value = responseData.cover_image_url;
      first_4_product_thumbnails.value = responseData.first_4_product_thumbnails;
      name.value = responseData.name;
      date.value = responseData.updated_at;
    } else {
      cover_image_url.value = responseData.thumbnail_url;
      name.value = responseData.title;
      date.value = responseData.updated_at;
    }

    is_loading.value = false;
  };

  const handleLoadContent = () => {
    // Check cache first
    if (loadFromCache()) {
      return;
    }
    let apiUrl = '';

    if (props.type === 'merchant') {
      apiUrl = props.content_type === 20 ? `/creator/get-anchor-collection?merchant_account_id=${props.merchant_id}&anchor_collection_id=${props.content_id}`
      : `/creator/get-anchor-post?merchant_account_id=${props.merchant_id}&anchor_post_id=${props.content_id}`;
    } else {
      apiUrl = props.content_type === 20 ? `/manage/get-anchor-collection?merchant_account_id=${props.merchant_id}&anchor_collection_id=${props.content_id}`
      : `/manage/get-anchor-post?merchant_account_id=${props.merchant_id}&anchor_post_id=${props.content_id}`;
    }



    props.ajax.get(apiUrl).then((res) => {
      const responseData = res.data.data;

      // Check if content exists and has id
      if (!responseData || typeof responseData !== 'object' || !responseData.id) {
        // Mark as deleted in cache
        markAsDeleted();
        name.value = 'Content has been deleted';
        is_loading.value = false;
        return;
      }

      handleContent(responseData);

      // Save to cache
      saveToCache(responseData);
      
      // console.log(content_data.value);
    }).catch((error) => {
      console.error('Failed to load content:', error);
      is_loading.value = false;
    });
  };
</script>

<style lang="scss">
  .message-content-item {
    margin-top: 8px;

    .#{$prefix}-button {
      margin: 10px auto 0;
      display: block;
    }

    .message-content-item-wrap {
      display: flex;
      gap: 8px;
      padding: 8px;
      background-color: white;
      border: 1px solid var(--Black-30);
      border-radius: 6px;

      .#{$prefix}-image,
      .collection-grid {
        width: 80px;
        height: 80px;
        position: relative;
      }
      
      .#{$prefix}-image {
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      &.is-merchant-item {
        border: 1px solid var(--Black-10);
      }
    }

    .message-content-item-details {
      
      h3 {
        margin: 0 0 4px;
        color: var(--Text-primary);
        font: var(--medium-14);
      }

      time {
        font: var(--medium-12);
        color: var(--Text-default);
        display: block;
        margin-bottom: 8px;
      }
    }

    .deleted-message-content-item-details {
      color: var(--Text-default);
      font: var(--medium-12);
    }
  }
</style>